<template>
  <div v-if="showAdminSitesCard">
    <v-sheet
      v-if="fetchingProfile"
      data-testid="adminSitesCard-sheet-skeleton"
      elevation="2"
      class="mt-4 py-4 px-6"
    >
      <v-skeleton-loader type="text" width="160" />
      <v-skeleton-loader type="chip@4" width="240" class="mt-4" />
    </v-sheet>
    <v-sheet
      v-else
      data-testid="adminSitesCard-sheet-card"
      rounded
      elevation="2"
      class="mt-4 py-4 px-6"
    >
      <div data-testid="adminSitesCard-section-managedSites">
        <p class="ma-0 pa-0 mb-2 subtitle-2" style="font-weight: 700">
          {{ $t('common.users.managed_sites') }}
        </p>

        <div data-testid="adminSitesCard-section-defaultSite">
          <p
            class="ma-0 pa-0 mb-1 text-caption blue-grey--text text--darken-1"
            data-testid="adminSitesCard-text-defaultSiteLabel"
          >
            {{ $t('organization_users.default_site') }}
          </p>
          <div class="d-flex flex-wrap">
            <v-chip
              v-if="defaultSite"
              class="me-1 mb-1"
              color="blue-grey"
              data-testid="adminSitesCard-chip-defaultSite"
              outlined
              pill
              small
            >
              <v-icon left size="16" color="blue-grey darken-1"
                >mdi-warehouse</v-icon
              >
              <span class="blue-grey--text text--darken-3">
                {{ defaultSite.name }}
              </span>
            </v-chip>
          </div>
        </div>

        <div
          v-if="hasAdditionalSites"
          class="mt-3"
          data-testid="adminSitesCard-section-additionalSites"
        >
          <p
            class="ma-0 pa-0 mb-1 text-caption blue-grey--text text--darken-1"
            data-testid="adminSitesCard-text-additionalSitesLabel"
          >
            {{ $t('common.users.additional_sites') }}
          </p>
          <div class="d-flex flex-wrap">
            <v-chip
              v-for="site in additionalSitesDetails"
              :key="site.id"
              class="me-1 mb-1"
              color="blue-grey"
              data-testid="adminSitesCard-chip-site"
              outlined
              pill
              small
            >
              <v-icon left size="16" color="blue-grey darken-1"
                >mdi-warehouse</v-icon
              >
              <span class="blue-grey--text text--darken-3">
                {{ site.name }}
              </span>
            </v-chip>
          </div>
        </div>
      </div>

      <div
        v-if="hasManagedSiteGroups"
        class="mt-4"
        data-testid="adminSitesCard-section-siteGroups"
      >
        <p class="ma-0 pa-0 mb-1 text-caption blue-grey--text text--darken-1">
          {{ $t('common.users.managed_site_groups') }}
        </p>
        <div class="d-flex flex-wrap">
          <v-chip
            v-for="siteGroup in managedSiteGroupsDetails"
            :key="siteGroup.id"
            class="me-1 mb-1"
            color="blue-grey"
            data-testid="adminSitesCard-chip-siteGroup"
            outlined
            pill
            small
          >
            <v-icon left size="16" color="blue-grey darken-1"
              >mdi-office-building</v-icon
            >
            <span class="blue-grey--text text--darken-3">
              {{ siteGroup.name }}
            </span>
          </v-chip>
        </div>
      </div>
    </v-sheet>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import { utils } from 'frontend-js-shared'

export default {
  name: 'AdminSitesCard',

  props: {
    profileAccount: {
      type: Object,
      required: true,
    },
    fetchingProfile: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    ...mapGetters(['organizationSiteGroups', 'organizationSites']),

    authorizedSiteIds() {
      return utils.getAuthorizedSiteIds({
        account: this.profileAccount,
        organizationSiteGroups: this.organizationSiteGroups,
      })
    },

    showAdminSitesCard() {
      const isMultiSiteAdmin =
        this.profileAccount?.isSiteAdmin && this.authorizedSiteIds.length > 1

      return Boolean(isMultiSiteAdmin)
    },

    defaultSite() {
      const site = this.profileAccount?.site
      const id = utils.getEntityId(site)
      if (!id) return null

      const name = site?.name ?? this.getOrganizationSiteName(id)
      return name ? { id, name } : null
    },

    additionalSitesDetails() {
      const defaultSiteId = this.defaultSite?.id
      const managedSites = this.profileAccount?.managedSites ?? []
      if (_.isEmpty(managedSites)) return []

      const sitesById = new Map()
      managedSites.forEach(site => {
        const id = utils.getEntityId(site)
        if (!id || id === defaultSiteId) return

        const name = site?.name ?? this.getOrganizationSiteName(id)
        if (!name) return

        sitesById.set(id, { id, name })
      })

      return [...sitesById.values()]
    },

    hasAdditionalSites() {
      return !_.isEmpty(this.additionalSitesDetails)
    },

    managedSiteGroupsDetails() {
      const managedGroups = this.profileAccount?.managedSiteGroups ?? []
      if (_.isEmpty(managedGroups)) return []

      return managedGroups
        .map(siteGroupRef => {
          const id = utils.getEntityId(siteGroupRef)
          const orgSiteGroup = this.organizationSiteGroups.find(
            candidate => utils.getEntityId(candidate) === id
          )

          return {
            id,
            name: siteGroupRef?.name ?? orgSiteGroup?.name,
          }
        })
        .filter(siteGroup => siteGroup.id && siteGroup.name)
    },

    hasManagedSiteGroups() {
      return !_.isEmpty(this.managedSiteGroupsDetails)
    },
  },

  methods: {
    getOrganizationSiteName(siteId) {
      const orgSite = this.organizationSites.find(
        candidate => utils.getEntityId(candidate) === siteId
      )

      return orgSite?.name
    },
  },
}
</script>
